import type { Address, Hex } from "viem";
import type { PrivateKeyAccount } from "viem/accounts";
import {
  H,
  bodyHash,
  randomHex,
  secretMessage,
  type ChatMessage,
  type IntentJson,
  type Receipt,
  type PauseInfo,
  type SettingsRecord,
  type Spend,
  type ConversationSummary,
  type Turn,
  challengeToSign,
  type ChallengeAnswer,
  handlePath,
  parseExtensionId,
  signRequest,
} from "@woodpecker/protocol";
import type { DeployForm, CardLine, StepState } from "@woodpecker/protocol";
import { saveSession, sessionOf, type Session } from "./wallet";

export class HttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly body: Record<string, unknown>,
  ) {
    super(`${status} ${code}`);
  }
}

/**
 * The text of a failed call for a toast: the status and the error name, then the detail the service sent with its
 * refusal (the runner names the service and both hashes of a price-document mismatch, issue #11 C).
 */
export function errorText(e: unknown): string {
  if (e instanceof HttpError) {
    const detail = e.body.detail;
    return typeof detail === "string" && detail ? `${e.message} · ${detail}` : e.message;
  }
  return e instanceof Error ? e.message : String(e);
}

export async function readBody(res: Response): Promise<Record<string, unknown>> {
  const text = await res.text();
  if (!text) return {};
  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    return { text };
  }
}

/**
 * The sign-in of D47 against a service's Durable Object: the challenge of the wallet's name, the signature of the
 * wallet itself or of an identity the wallet authorized on contract A (D47a, D73), a session credential kept for
 * the wallet. The Console signs only the message it rebuilds from the challenge's fields, for the service it meant
 * to reach at the host it reached: a service never gets a signature over a text of its own choosing (0.2).
 */
export async function signInTo(
  origin: string,
  account: PrivateKeyAccount,
  chainId: number,
  opts: { service: string; wallet?: Address },
): Promise<{ session: Session; challenge: { nonce: Hex; domain: string; expires: string; service: string } }> {
  const wallet = opts.wallet ?? account.address;
  const name = `${chainId}:${wallet.toLowerCase()}`;
  const cRes = await fetch(`${origin}/${name}/challenge`, { method: "POST" });
  if (!cRes.ok) throw new HttpError(cRes.status, "challenge", await readBody(cRes));
  const c = (await cRes.json()) as ChallengeAnswer;
  let message: string;
  try {
    message = challengeToSign(c, { service: opts.service, wallet: name, domain: new URL(origin).host });
  } catch (e) {
    throw new HttpError(400, "bad_challenge", { detail: e instanceof Error ? e.message : String(e) });
  }
  const signature = await account.signMessage({ message });
  const sRes = await fetch(`${origin}/${name}/session`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ nonce: c.nonce, signature }),
  });
  if (!sRes.ok) throw new HttpError(sRes.status, "session", await readBody(sRes));
  const s = (await sRes.json()) as Session;
  // a session an identity signed in for is remembered as such: the wallet's own sessions register the Console identity
  const session: Session = opts.wallet ? { ...s, identity: account.address } : s;
  saveSession(origin, wallet, session);
  return { session, challenge: c };
}

/** A conversation as the runner's DO returns it (CONTRACT.md 5.1). */
export interface ConversationView {
  id: string;
  kind: "conversation" | "deploy";
  n: number;
  agent: Address;
  title: string;
  runner: string;
  memory: string;
  model: string; // gateway|model
  tools: string[];
  effort: string;
  caps: Record<string, number>;
  budget?: string;
  status: "idle" | "running" | "paused" | "stopped" | "done";
  pause?: PauseInfo;
  stopped: boolean;
  createdAt: string;
  lastActivity: string;
  turn: number;
  messages: ChatMessage[];
  spends: Spend[]; // newest first
  open: { runner: number; memory: number };
  turnState?: { turn: number; phase: string; round: number; roundPhase: string; spends: Spend[] };
  deploy?: {
    form: DeployForm;
    keyFingerprint: string | null;
    deploymentId: string;
    host: string;
    memory: string;
    phase: string;
    steps: Record<string, StepState>;
    origin?: string;
    submitter?: Address;
    tokenAddress?: Address;
    testAgentN?: number;
    testAgent?: Address;
    serviceName: string;
    card?: {
      name: "contracts" | "verify";
      lines: CardLine[];
      state: "pending" | "signed" | "rejected";
      txs?: Array<{ call: string; tx: string }>;
    };
    error?: string;
  };
  eventSeq: number;
}

export interface RunnerEvent {
  seq: number;
  type: string;
  at: string;
  data: unknown;
}

/** A write to an extension's namespace, pending with its receipt (D74, D75). */
export interface ExtConfigView {
  id: string;
  identity: Address | null;
  quota: number | null;
  config: Record<string, unknown>;
  updatedAt: string | null;
  bytes: number;
}

abstract class OwnerClient {
  constructor(
    readonly origin: string,
    readonly wallet: Address,
    readonly chainId: number,
  ) {}

  get name(): string {
    return `${this.chainId}:${this.wallet.toLowerCase()}`;
  }

  get session(): Session | null {
    return sessionOf(this.origin, this.wallet);
  }

  protected async call<T>(
    method: string,
    path: string,
    body?: unknown,
    headers: Record<string, string> = {},
  ): Promise<T> {
    const s = this.session;
    if (!s) throw new HttpError(401, "no_session", {});
    const raw = body instanceof Uint8Array;
    const res = await fetch(`${this.origin}/${this.name}${path}`, {
      method,
      headers: {
        authorization: `Bearer ${s.credential}`,
        ...(body !== undefined && !raw ? { "content-type": "application/json" } : {}),
        ...headers,
      },
      body: body === undefined ? undefined : raw ? (body as unknown as BodyInit) : JSON.stringify(body),
    });
    const data = await readBody(res);
    if (!res.ok) throw new HttpError(res.status, String(data.error ?? res.status), data);
    return data as T;
  }

  /** The Console identity this service holds for the wallet (D73), or null when none is registered (or the service predates it). */
  async consoleIdentity(): Promise<Address | null> {
    const res = await fetch(`${this.origin}/${this.name}/console`);
    if (res.status === 404) return null;
    if (!res.ok) throw new HttpError(res.status, "console", await readBody(res));
    return ((await res.json()) as { identity: Address }).identity;
  }

  /** The wallet registers its Console identity over its own session: that identity's sessions are the owner's from then on. */
  registerConsole(identity: Address) {
    return this.call<{ identity: Address }>("PUT", "/console", { identity });
  }
}

/** A client of any service's owner API for the routes every service has: the console identity (D73). */
export class ServiceClient extends OwnerClient {}

/** A file as a store lists it (D74). */
export interface StoreFileView {
  name: string;
  size: number;
  type: string;
  hash: Hex;
  updatedAt: string;
  state: "confirmed" | "pending";
  tx?: Hex;
}

/**
 * A store service's owner API (D34a, D74): the list and the files free over the session; a put is a paid write of
 * the extension's identity (the four headers over the bytes), pending until its settle at `<file>/settle`.
 */
export class StoreClient extends OwnerClient {
  constructor(
    origin: string,
    wallet: Address,
    chainId: number,
    readonly service: string,
  ) {
    super(origin, wallet, chainId);
  }

  private base(id: string): string {
    return `/ext/${id}/files`;
  }

  list(id: string) {
    return this.call<{ id: string; identity: Address | null; quota: number; used: number; files: StoreFileView[] }>(
      "GET",
      this.base(id),
    );
  }

  /** The bytes of a confirmed file. */
  async file(id: string, name: string): Promise<Response> {
    const s = this.session;
    if (!s) throw new HttpError(401, "no_session", {});
    const res = await fetch(`${this.origin}/${this.name}${this.base(id)}/${encodeURIComponent(name)}`, {
      headers: { authorization: `Bearer ${s.credential}` },
      cache: "no-store",
    });
    if (!res.ok) {
      const data = await readBody(res);
      throw new HttpError(res.status, String(data.error ?? res.status), data);
    }
    return res;
  }

  delete(id: string, name: string) {
    return this.call<unknown>("DELETE", `${this.base(id)}/${encodeURIComponent(name)}`);
  }

  /** The owner binds the namespace to the extension's identity (issue #21): at install, and when a tab finds it unbound. */
  bind(id: string, identity: Address) {
    return this.call<{ id: string; identity: Address; since: string }>("PUT", `/ext/${id}/binding`, { identity });
  }

  /** Uninstall unbinds; the files stay for the owner. */
  unbind(id: string) {
    return this.call<unknown>("DELETE", `/ext/${id}/binding`);
  }

  /** Step one of a put: pending, with the store's receipt (1 of its token per MiB started). */
  async putPending(
    id: string,
    name: string,
    bytes: Uint8Array,
    opts: { payer: PrivateKeyAccount; type?: string; call?: string },
  ): Promise<{ state: "pending"; call: string; receipt: Receipt }> {
    const call = opts.call ?? randomHex(16);
    return this.call<{ state: "pending"; call: string; receipt: Receipt }>(
      "PUT",
      `${this.base(id)}/${encodeURIComponent(name)}`,
      bytes,
      {
        [H.client]: this.wallet,
        [H.identity]: opts.payer.address,
        [H.call]: call,
        [H.signature]: await signRequest(opts.payer, this.service, call, bytes),
        [H.close]: "1",
        "content-type": opts.type ?? "application/octet-stream",
      },
    );
  }

  settlePut(id: string, name: string, calls: string[], signed: { intent: IntentJson; signature: Hex }) {
    return this.call<{ state: "confirmed"; tx: Hex; file: StoreFileView }>(
      "POST",
      `${this.base(id)}/${encodeURIComponent(name)}/settle`,
      { ...signed, calls },
    );
  }
}

export class RunnerClient extends OwnerClient {
  state() {
    return this.call<{
      wallet: Address;
      chainId: number;
      agents: Array<{ conversationId: string; agent: Address; n: number; kind: string; status: string }>;
      running: string[];
    }>("GET", "/state");
  }
  list() {
    return this.call<ConversationView[]>("GET", "/conversations");
  }
  get(id: string) {
    return this.call<ConversationView>("GET", `/conversations/${id}`);
  }
  create(body: {
    memory: string;
    model: string;
    tools?: string[];
    effort?: string;
    caps?: Record<string, number>;
    budget?: string;
  }) {
    return this.call<{ conversationId: string; agent: Address; n: number }>("POST", "/conversations", body);
  }
  message(id: string, text: string) {
    return this.call<{ turn: number }>("POST", `/conversations/${id}/messages`, { text });
  }
  events(id: string, after: number, wait: number, signal?: AbortSignal) {
    const s = this.session;
    if (!s) throw new HttpError(401, "no_session", {});
    return fetch(`${this.origin}/${this.name}/conversations/${id}/events?after=${after}&wait=${wait}`, {
      headers: { authorization: `Bearer ${s.credential}` },
      signal,
    }).then(async (res) => {
      const data = await readBody(res);
      if (!res.ok) throw new HttpError(res.status, String(data.error ?? res.status), data);
      return data as unknown as { events: RunnerEvent[]; status: string; pause?: PauseInfo; seq: number };
    });
  }
  resume(id: string) {
    return this.call<unknown>("POST", `/conversations/${id}/resume`);
  }
  stop(id: string) {
    return this.call<ConversationView>("POST", `/conversations/${id}/stop`);
  }
  retry(id: string) {
    return this.call<unknown>("POST", `/conversations/${id}/retry`);
  }
  patch(id: string, body: { effort?: string; title?: string }) {
    return this.call<ConversationView>("PATCH", `/conversations/${id}`, body);
  }
  createDeployment(body: {
    form: DeployForm;
    key: { fingerprint: string | null; deployment?: string };
    model: string;
    memory: string;
    host: string;
    caps: Record<string, number>;
  }) {
    return this.call<{ conversationId: string; agent: Address; n: number; serviceName: string }>(
      "POST",
      "/deployments",
      body,
    );
  }
  card(
    id: string,
    name: string,
    body: { signed?: boolean; rejected?: boolean; txs?: Array<{ kind: string; tx: string }>; tokenAddress?: Address },
  ) {
    return this.call<unknown>("POST", `/conversations/${id}/cards/${name}`, body);
  }
  reverify(id: string) {
    return this.call<unknown>("POST", `/conversations/${id}/reverify`);
  }
}

export class MemoryClient extends OwnerClient {
  settings() {
    return this.call<SettingsRecord>("GET", "/settings");
  }
  /**
   * Step one of a settings write (D75): the record goes in pending and the memory answers with its receipt. A plain
   * PUT merges the body into the record; `replace` writes the body as the whole record (the switch's writes).
   */
  putSettingsPending(body: Partial<SettingsRecord>, opts: { replace?: boolean; payer: Address; call?: string }) {
    return this.call<{ state: "pending"; call: string; receipt: Receipt }>(
      "PUT",
      opts.replace ? "/settings?replace=true" : "/settings",
      body,
      { [H.identity]: opts.payer, [H.call]: opts.call ?? randomHex(16) },
    );
  }
  /** Step two: the Intent the Console's identity signed; the memory's submitter settles and confirms the writes. */
  settleSettings(calls: string[], signed: { intent: IntentJson; signature: Hex }) {
    return this.call<{ state: "confirmed"; tx: Hex; settings: SettingsRecord }>("POST", "/settings/settle", {
      ...signed,
      calls,
    });
  }
  list() {
    return this.call<ConversationSummary[]>("GET", "/conversations");
  }
  /** An extension's configuration namespace (D74): the owner reads it free. */
  extConfig(id: string) {
    return this.call<ExtConfigView>("GET", `/ext/${id}/config`);
  }
  /** Step one of a write to the namespace: pending, with the memory's receipt; the extension's identity pays. */
  putExtConfigPending(id: string, config: Record<string, unknown>, opts: { payer: Address; call?: string }) {
    return this.call<{ state: "pending"; call: string; receipt: Receipt }>("PUT", `/ext/${id}/config`, config, {
      [H.identity]: opts.payer,
      [H.call]: opts.call ?? randomHex(16),
    });
  }
  settleExtConfig(id: string, calls: string[], signed: { intent: IntentJson; signature: Hex }) {
    return this.call<{ state: "confirmed"; tx: Hex; config: Record<string, unknown> }>(
      "POST",
      `/ext/${id}/config/settle`,
      { ...signed, calls },
    );
  }
  read(id: string) {
    return this.call<{
      id: string;
      kind: string;
      title: string;
      agent: Address;
      runner: string;
      memory: string;
      model: string;
      tools: string[];
      turns: Turn[];
      messages: ChatMessage[];
      spends: Spend[];
    }>("GET", `/conversations/${id}`);
  }
  scan(q: string) {
    return this.call<Array<{ id: string; title: string; turn: number; text: string }>>(
      "GET",
      `/scan?q=${encodeURIComponent(q)}`,
    );
  }
}

/** The upstream key from the browser straight to the host's secret store: a wallet-signed PUT (D17). */
export async function putUpstreamKey(
  origin: string,
  service: string,
  account: PrivateKeyAccount,
  deployment: string,
  value: string,
): Promise<{ fingerprint: string }> {
  const time = new Date().toISOString();
  const signature = await account.signMessage({ message: secretMessage(service, deployment, time, bodyHash(value)) });
  const res = await fetch(`${origin}/secrets/deployments/${deployment}/UPSTREAM_KEY`, {
    method: "PUT",
    headers: {
      [H.wallet]: account.address,
      [H.walletTimestamp]: time,
      [H.walletSignature]: signature,
      "content-type": "text/plain",
    },
    body: value,
  });
  const data = await readBody(res);
  if (!res.ok) throw new HttpError(res.status, String(data.error ?? res.status), data);
  return data as { fingerprint: string };
}

/** sha256 of the key's value, the first 12 hex characters: what the agent sees (the host's fingerprintOf). */
export async function fingerprintOf(value: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, 12);
}

/** A version of an extension at the host, as its `versions` route lists it. */
export interface PackageVersion {
  version: string;
  hash: Hex;
  url: string;
  size: number;
  state: "pending" | "confirmed";
  uploadedAt: string;
  confirmedAt?: string;
  tx?: Hex;
}

/**
 * The host's package routes (D71): the upload of a package as a paid call of the Console's identity (the four
 * headers over the zip), its settle, the versions the host keeps. No session: the identity's signature authenticates.
 */
export class HostPackageClient {
  constructor(
    readonly origin: string,
    readonly service: string,
    readonly wallet: Address,
  ) {}

  private base(id: string): string {
    const p = parseExtensionId(id);
    if (!p) throw new Error(`bad extension id ${id}`);
    return `${this.origin}/ext/${handlePath(p.handle)}/${p.name}`;
  }

  private async paid(
    payer: PrivateKeyAccount,
    call: string,
    body: string | Uint8Array,
  ): Promise<Record<string, string>> {
    return {
      [H.client]: this.wallet,
      [H.identity]: payer.address,
      [H.call]: call,
      [H.signature]: await signRequest(payer, this.service, call, body),
      [H.close]: "1",
    };
  }

  async uploadPending(
    id: string,
    zip: Uint8Array,
    payer: PrivateKeyAccount,
    call: string = randomHex(16),
  ): Promise<{ state: "pending"; call: string; hash: Hex; receipt: Receipt }> {
    const res = await fetch(`${this.base(id)}/packages`, {
      method: "POST",
      headers: { ...(await this.paid(payer, call, zip)), "content-type": "application/zip" },
      body: zip as unknown as BodyInit,
    });
    const data = await readBody(res);
    if (!res.ok) throw new HttpError(res.status, String(data.error ?? res.status), data);
    return data as { state: "pending"; call: string; hash: Hex; receipt: Receipt };
  }

  async settleUpload(
    id: string,
    calls: string[],
    signed: { intent: IntentJson; signature: Hex },
    payer: PrivateKeyAccount,
  ): Promise<{ state: "confirmed"; url: string; hash: Hex; version: string; tx: Hex }> {
    const body = JSON.stringify({ ...signed, calls });
    const res = await fetch(`${this.base(id)}/packages/settle`, {
      method: "POST",
      headers: { ...(await this.paid(payer, `${calls[0]}:settle`, body)), "content-type": "application/json" },
      body,
    });
    const data = await readBody(res);
    if (!res.ok) throw new HttpError(res.status, String(data.error ?? res.status), data);
    return data as { state: "confirmed"; url: string; hash: Hex; version: string; tx: Hex };
  }

  async versions(id: string): Promise<PackageVersion[]> {
    const res = await fetch(`${this.base(id)}/versions`, { cache: "no-store" });
    if (!res.ok) return [];
    return (await res.json()) as PackageVersion[];
  }
}
